import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AnimatePresence,
  motion,
  useReducedMotion,
  useScroll,
} from 'framer-motion';
import HeroParticles from '../components/HeroParticles';
import useScrollReveal from '../hooks/useScrollReveal';
import './HomePage.css';
import {bavani_image,danayash_image, jeya_image, swaroop_image, veerbabu_image, saisrikar_image, vijayashwanth_image, satya_image} from './assesst';

const RUSIV_URL = 'https://rusiv.com';

const ArrowIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M5 12h14M13 6l6 6-6 6" />
  </svg>
);

const SendIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M22 2 11 13" />
    <path d="m22 2-7 20-4-9-9-4 20-7Z" />
  </svg>
);

const SearchIcon = () => (
  <svg
    viewBox="0 0 24 24"
    aria-hidden="true"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="11" cy="11" r="8" />
    <path d="m21 21-4.35-4.35" />
  </svg>
);

const ChevronIcon = ({ direction = 'right' }) => (
  <svg
    viewBox="0 0 24 24"
    aria-hidden="true"
    style={{ transform: direction === 'left' ? 'rotate(180deg)' : undefined }}
  >
    <path d="M5 12h14M13 6l6 6-6 6" />
  </svg>
);

const commitments = [
  {
    num: '01',
    title: 'Personal by Design',
    desc: 'Built to understand you specifically, not a generic version of a user.',
  },
  {
    num: '02',
    title: 'Continuous Understanding',
    desc: 'Every useful conversation adds to what Rusiv understands about how you think.',
  },
  {
    num: '03',
    title: 'Adaptive Communication',
    desc: 'Tone, depth, and structure shift based on what the moment actually needs.',
  },
  {
    num: '04',
    title: 'You Stay in Control',
    desc: 'Transparency and consent guide how your context is used and remembered.',
  },
];

const capabilities = [
  {
    num: '01',
    title: 'A Quick Answer',
    desc: 'Some days you need the fact, not the full explanation behind it.',
  },
  {
    num: '02',
    title: 'A Deeper Dive',
    desc: 'Other days you want the reasoning, the sources, and the whole picture.',
  },
  {
    num: '03',
    title: 'A New Concept',
    desc: 'Learning something unfamiliar calls for an analogy, not a dictionary definition.',
  },
  {
    num: '04',
    title: 'A Tough Call',
    desc: 'A real decision calls for trade-offs and context, not a quick opinion.',
  },
  {
    num: '05',
    title: 'A Rough Day',
    desc: 'Some moments need patience and understanding before they need advice.',
  },
  {
    num: '06',
    title: 'A Familiar Voice',
    desc: 'Return later and Rusiv can continue from the point that actually mattered.',
  },
];

const productChatSequence = [
  [1, 900],
  [2, 1200],
  [3, 1700],
  [4, 1800],
  [5, 1200],
  [6, 1800],
];

const buildPrinciples = [
  {
    title: 'Understand',
    eyebrow: 'Signal 01',
    text: 'Read the situation behind the words and identify what the person is actually trying to do.',
    status: 'Intent resolved',
  },
  {
    title: 'Remember',
    eyebrow: 'Signal 02',
    text: 'Bring forward only the earlier context that can genuinely improve the present conversation.',
    status: 'Relevant context found',
  },
  {
    title: 'Reason',
    eyebrow: 'Signal 03',
    text: 'Work through the problem, compare possibilities, and choose the most useful path before answering.',
    status: 'Approach selected',
  },
  {
    title: 'Verify',
    eyebrow: 'Signal 04',
    text: 'Use current information and specialised tools when the answer needs evidence beyond the conversation.',
    status: 'Confidence checked',
  },
];

/* Sample-only testimonial content and portrait photography.
   Replace every item with approved customer stories and owned/licensed images
   before publishing the section as real social proof. */
const testimonials = [
  {
    quote:
      'Rusiv has a personality of its own. The conversations are fun, natural, and sometimes sarcastic, which makes it feel refreshingly different from typical AI.',
    name: 'Petla Veerendra babu',
    role: 'Senior Consultant - Infosys',
    context: 'Sample story',
    image:
      veerbabu_image,
  },
  {
    quote:
      'Rusiv oda responses quick ah irukku, use panradhum smooth ah feel aagudhu. Simple questions ku unnecessary ah periya answers kudukkaradhu illa.',
    name: 'Dhiyanesh Shankar',
    role: 'Investment Banker - Wipro',
    // context: 'Sample story',
    image:danayash_image,
  },
  {
    quote:
      'Rusiv felt easy to use from the beginning. The more I interacted with it, the more personal the conversations started to feel..',
    name: 'Kotturu Bhavani',
    role: 'HRP Analyst - Cognizant',
    // context: 'Sample story',
    image:
      bavani_image,
  },
  {
    quote:
      'Rusiv understands me surprisingly well, even across different languages. I can speak naturally without worrying about framing the perfect prompt.',
    name: 'Battu Jeyavardhini',
    role: 'Programer Analyst - Cognizant',
    // context: 'Sample story',
    image:
      jeya_image,
  },
    {
    quote:
      'I love that Rusiv doesn’t overwhelm me with huge answers. It gives me what actually matters and keeps the conversation simple and useful.',
    name: 'Ananthula Sai Ram Swaroop',
    role: 'Data Engineer - Accenture',
    context: 'Sample story',
    image:
      swaroop_image,
  },
  {
    quote:
      'Rusiv kitta perfect ah prompt type panna vendiya avasiyam illa. Normal ah pesinaalum namma enna solla varom nu purinjukittu respond pannudhu',
    name: 'Vijay Ashwanth',
    role: 'Data Analyst - OLA Krutrim',
    // context: 'Sample story',
    image:
      vijayashwanth_image,
  },
  {
    quote:
      'Rusiv context ni chaala baaga understand cheskuntundi. Mundhu matladina vishayalani current conversation tho connect chesi reply ivvadam naaku baaga nachindhi.',
    name: 'Sistla Sai Srikar',
    role: 'M.Tech Student - SRM University',
    // context: 'Sample story',
    image:
      saisrikar_image,
  },
  {
    quote:
      'Rusiv sarcasm vere level 😂. Casual ga matladithe fun ga untundi, serious ga adigithe useful information kuda correct ga isthundi',
    name: 'Lanka Dhana Satya Sri',
    role: 'BSC Student - VIT',
    // context: 'Sample story',
    image:
      satya_image,
  },
];

const slugify = (text) =>
  text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');

const faqs = [
  {
    question: 'What is Allyverto building?',
    answer:
      'Allyverto is the company building Rusiv, a personal AI designed to understand how a person communicates, learns, and thinks, then adapt its responses accordingly.',
  },
  {
    question: 'What makes Rusiv different from other AI assistants?',
    answer:
      'A typical assistant mainly answers the message in front of it. Rusiv is being designed to also use relevant context about the person so tone, depth, structure, memory, and tools can better match the current need.',
  },
  {
    question: 'How does Rusiv get to know me?',
    answer:
      'Rusiv can begin with an intentional introduction and then refine its understanding through real interactions. The aim is to keep that process visible and controllable rather than silently collecting everything.',
  },
  {
    question: 'Does Rusiv remember previous conversations?',
    answer:
      'Rusiv is being designed around continuity and relevant memory. Earlier information should be brought forward only when it can genuinely improve the present conversation and when the user has appropriate control over it.',
  },
  {
    question: 'Can Rusiv use live information and tools?',
    answer:
      'Rusiv can use specialised capabilities such as web search, document understanding, calculation, and other tools when a question requires evidence or work beyond generated text.',
  },
  {
    question: 'When can I try Rusiv?',
    answer:
      'An early version is available at rusiv.com. Rusiv remains in active development, so capabilities and the overall experience will continue to evolve.',
  },
  {
    question: 'How does Allyverto think about safety and privacy?',
    answer:
      'The direction is to make personal intelligence understandable and controllable, require appropriate permission for sensitive actions, and communicate uncertainty instead of presenting unverified information as certain.',
  },
  {
    question: "What is Allyverto's long term vision?",
    answer:
      'Education is an important starting point, but the longer term goal is personal intelligence that remains useful across learning, work, decisions, and everyday life while staying shaped around the person using it.',
  },
];

const sectionReveal = {
  hidden: { opacity: 0, y: 28 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.75, ease: [0.22, 1, 0.36, 1] },
  },
};

const handleSpatialMove = (event) => {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const element = event.currentTarget;
  const rect = element.getBoundingClientRect();
  const x = (event.clientX - rect.left) / rect.width - 0.5;
  const y = (event.clientY - rect.top) / rect.height - 0.5;

  element.style.setProperty('--tilt-x', `${(-y * 5).toFixed(2)}deg`);
  element.style.setProperty('--tilt-y', `${(x * 6).toFixed(2)}deg`);
  element.style.setProperty('--pointer-x', `${((x + 0.5) * 100).toFixed(1)}%`);
  element.style.setProperty('--pointer-y', `${((y + 0.5) * 100).toFixed(1)}%`);
};

const handleSpatialLeave = (event) => {
  const element = event.currentTarget;
  element.style.setProperty('--tilt-x', '0deg');
  element.style.setProperty('--tilt-y', '0deg');
  element.style.setProperty('--pointer-x', '50%');
  element.style.setProperty('--pointer-y', '50%');
};


const STORY_X_RATIOS = [0.88, 0.74, 0.84, 0.58, 0.28, 0.2, 0.42, 0.78, 0.62];

const StorySignal = ({ targetRef }) => {
  const reducedMotion = useReducedMotion();
  const [geometry, setGeometry] = useState({
    width: 1200,
    height: 1,
    path: 'M 1056 0 L 1056 1',
    nodes: [],
  });

  const { scrollYProgress } = useScroll({
    target: targetRef,
    offset: ['start 78%', 'end 22%'],
  });

  useEffect(() => {
    const target = targetRef.current;
    if (!target) return undefined;

    let frameId = 0;
    let disposed = false;

    const measure = () => {
      frameId = 0;
      if (disposed) return;

      const targetRect = target.getBoundingClientRect();
      const width = Math.max(320, Math.round(targetRect.width));
      const height = Math.max(1, Math.round(target.scrollHeight));
      const anchors = Array.from(
        target.querySelectorAll('[data-story-anchor]')
      );

      const anchorPoints = anchors.map((element, index) => {
        const rect = element.getBoundingClientRect();
        const y = rect.top - targetRect.top + rect.height * 0.5;
        const ratio =
          STORY_X_RATIOS[index + 1] ??
          STORY_X_RATIOS[STORY_X_RATIOS.length - 2];

        return {
          x: Math.round(width * ratio),
          y: Math.max(0, Math.min(height, Math.round(y))),
        };
      });

      const points = [
        { x: Math.round(width * STORY_X_RATIOS[0]), y: 0 },
        ...anchorPoints,
        {
          x: Math.round(
            width * STORY_X_RATIOS[STORY_X_RATIOS.length - 1]
          ),
          y: height,
        },
      ];

      let path = `M ${points[0].x} ${points[0].y}`;

      for (let index = 1; index < points.length; index += 1) {
        const previous = points[index - 1];
        const current = points[index];
        const distanceY = Math.max(1, current.y - previous.y);
        const curve = Math.max(48, distanceY * 0.42);

        path +=
          ` C ${previous.x} ${previous.y + curve}` +
          ` ${current.x} ${current.y - curve}` +
          ` ${current.x} ${current.y}`;
      }

      setGeometry({
        width,
        height,
        path,
        nodes: anchorPoints,
      });
    };

    const scheduleMeasure = () => {
      if (disposed) return;
      if (frameId) window.cancelAnimationFrame(frameId);
      frameId = window.requestAnimationFrame(measure);
    };

    const resizeObserver = new ResizeObserver(scheduleMeasure);
    resizeObserver.observe(target);

    target
      .querySelectorAll('[data-story-anchor]')
      .forEach((element) => resizeObserver.observe(element));

    window.addEventListener('resize', scheduleMeasure, { passive: true });
    document.fonts?.ready?.then(scheduleMeasure);
    scheduleMeasure();

    return () => {
      disposed = true;
      if (frameId) window.cancelAnimationFrame(frameId);
      resizeObserver.disconnect();
      window.removeEventListener('resize', scheduleMeasure);
    };
  }, [targetRef]);

  const progress = reducedMotion ? 1 : scrollYProgress;

  return (
    <div className="hp-story-signal" aria-hidden="true">
      <svg
        viewBox={`0 0 ${geometry.width} ${geometry.height}`}
        preserveAspectRatio="none"
      >
        <path className="hp-story-signal-base" d={geometry.path} />

        <motion.path
          className="hp-story-signal-glow"
          d={geometry.path}
          style={{ pathLength: progress }}
        />

        <motion.path
          className="hp-story-signal-active"
          d={geometry.path}
          style={{ pathLength: progress }}
        />

        {geometry.nodes.map((node, index) => (
          <circle
            className={`hp-story-node hp-story-node-${index + 1}`}
            cx={node.x}
            cy={node.y}
            r="10"
            key={`${node.x}-${node.y}-${index}`}
          />
        ))}
      </svg>
    </div>
  );
};

const BuildExperience = () => {
  const reducedMotion = useReducedMotion();
  const [activeIndex, setActiveIndex] = useState(0);
  const active = buildPrinciples[activeIndex];

  return (
    <motion.div
      className="hp-build-experience"
      variants={sectionReveal}
      initial={reducedMotion ? 'visible' : 'hidden'}
      whileInView="visible"
      viewport={{ once: true, amount: 0.24 }}
    >
      <div
        className="hp-build-map"
        data-active={activeIndex}
        onPointerMove={handleSpatialMove}
        onPointerLeave={handleSpatialLeave}
      >
        <svg className="hp-build-connectors" viewBox="0 0 640 520" aria-hidden="true">
          <path className={activeIndex === 0 ? 'is-active' : ''} d="M170 130 C250 160 245 225 320 260" />
          <path className={activeIndex === 1 ? 'is-active' : ''} d="M470 130 C390 160 395 225 320 260" />
          <path className={activeIndex === 2 ? 'is-active' : ''} d="M470 390 C390 360 395 295 320 260" />
          <path className={activeIndex === 3 ? 'is-active' : ''} d="M170 390 C250 360 245 295 320 260" />
        </svg>

        <div className="hp-build-core-shell">
          <div className="hp-build-orbit hp-build-orbit-one" />
          <div className="hp-build-orbit hp-build-orbit-two" />

          <motion.div
            className="hp-build-core"
            animate={
              reducedMotion
                ? undefined
                : { scale: [1, 1.018, 1], rotate: [0, 0.35, 0] }
            }
            transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
          >
            <div className="hp-build-pair">
              <span>You</span>
              <span className="hp-build-pair-signal">
                <i />
              </span>
              <span>Intelligence</span>
            </div>

            <AnimatePresence mode="wait">
              <motion.div
                className="hp-build-core-copy"
                key={active.title}
                initial={reducedMotion ? false : { opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={reducedMotion ? undefined : { opacity: 0, y: -8 }}
                transition={{ duration: 0.28 }}
              >
                <small>{active.eyebrow}</small>
                <strong>{active.title}</strong>
                <span>{active.status}</span>
              </motion.div>
            </AnimatePresence>
          </motion.div>
        </div>

        {buildPrinciples.map((item, index) => (
          <button
            type="button"
            className={`hp-build-node hp-build-node-${index + 1} ${
              activeIndex === index ? 'is-active' : ''
            }`}
            key={item.title}
            onMouseEnter={() => setActiveIndex(index)}
            onFocus={() => setActiveIndex(index)}
            onClick={() => setActiveIndex(index)}
            aria-pressed={activeIndex === index}
          >
            <span>{String(index + 1).padStart(2, '0')}</span>
            {item.title}
          </button>
        ))}
      </div>

      <div className="hp-build-detail" aria-live="polite">
        <AnimatePresence mode="wait">
          <motion.div
            key={active.title}
            initial={reducedMotion ? false : { opacity: 0, x: 18 }}
            animate={{ opacity: 1, x: 0 }}
            exit={reducedMotion ? undefined : { opacity: 0, x: -14 }}
            transition={{ duration: 0.34 }}
          >
            <span className="hp-experience-kicker">{active.eyebrow}</span>
            <h3>{active.title}</h3>
            <p>{active.text}</p>
            <div className="hp-experience-status">
              <i />
              {active.status}
            </div>
          </motion.div>
        </AnimatePresence>

        <div className="hp-build-selector" role="tablist" aria-label="Allyverto intelligence principles">
          {buildPrinciples.map((item, index) => (
            <button
              type="button"
              role="tab"
              aria-selected={activeIndex === index}
              className={activeIndex === index ? 'is-active' : ''}
              key={item.title}
              onClick={() => setActiveIndex(index)}
            >
              {item.title}
            </button>
          ))}
        </div>
      </div>
    </motion.div>
  );
};

// Speed the rail auto-advances at, in pixels per 16.66ms (~60fps) frame.
const AUTO_SCROLL_SPEED = 0.5;
// How long a manual interaction (arrow click, drag, hover) pauses
// auto-scroll before it resumes on its own.
const AUTO_SCROLL_RESUME_MS = 2600;

const TestimonialRail = () => {
  const reducedMotion = useReducedMotion();
  const railRef = useRef(null);
  const dragState = useRef({ dragging: false, startX: 0, startScrollLeft: 0, moved: false });
  const [isDragging, setIsDragging] = useState(false);
  const pausedRef = useRef(false);
  const hoveredRef = useRef(false);
  const resumeTimerRef = useRef(null);
  const loopWidthRef = useRef(0);
  const cardWidthRef = useRef(0);

  const loopedTestimonials = [...testimonials, ...testimonials];

  const pauseAutoScroll = useRef((resumeAfter = AUTO_SCROLL_RESUME_MS) => {
    pausedRef.current = true;
    if (resumeTimerRef.current) window.clearTimeout(resumeTimerRef.current);
    if (resumeAfter !== null) {
      resumeTimerRef.current = window.setTimeout(() => {
        if (!hoveredRef.current) pausedRef.current = false;
      }, resumeAfter);
    }
  }).current;

  const scrollRail = useRef((direction) => {
    const rail = railRef.current;
    if (!rail) return;
    pauseAutoScroll();

    if (cardWidthRef.current === 0) {
      const card = rail.querySelector('.hp-testimonial-card');
      cardWidthRef.current = card?.getBoundingClientRect().width ?? 380;
    }
    const cardWidth = cardWidthRef.current;
    const gap = 20;

    rail.scrollBy({
      left: direction * (cardWidth + gap),
      behavior: reducedMotion ? 'auto' : 'smooth',
    });
  }).current;

  const handlePointerDown = useRef((event) => {
    if (event.pointerType === 'touch') return;
    const rail = railRef.current;
    if (!rail) return;

    pauseAutoScroll();
    dragState.current = {
      dragging: true,
      startX: event.clientX,
      startScrollLeft: rail.scrollLeft,
      moved: false,
    };
    setIsDragging(true);
  }).current;

  const handlePointerMove = useRef((event) => {
    const rail = railRef.current;
    if (!rail || !dragState.current.dragging) return;

    const delta = event.clientX - dragState.current.startX;
    if (Math.abs(delta) > 3) dragState.current.moved = true;
    rail.scrollLeft = dragState.current.startScrollLeft - delta;
  }).current;

  const endDrag = useRef(() => {
    dragState.current.dragging = false;
    setIsDragging(false);
  }).current;

  const suppressClickAfterDrag = useRef((event) => {
    if (dragState.current.moved) {
      event.preventDefault();
      event.stopPropagation();
    }
  }).current;

  const handleMouseEnter = useRef(() => {
    hoveredRef.current = true;
    pauseAutoScroll(null);
  }).current;

  const handleMouseLeave = useRef(() => {
    hoveredRef.current = false;
    pauseAutoScroll();
  }).current;

  useEffect(() => {
    const rail = railRef.current;
    if (!rail) return undefined;

    const measure = () => {
      loopWidthRef.current = rail.scrollWidth / 2;
      if (cardWidthRef.current === 0) {
        const card = rail.querySelector('.hp-testimonial-card');
        cardWidthRef.current = card?.getBoundingClientRect().width ?? 380;
      }
    };
    measure();

    if (!('ResizeObserver' in window)) return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(rail);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (reducedMotion) return undefined;
    const rail = railRef.current;
    if (!rail) return undefined;

    let frameId;
    let lastTime = null;
    const loopWidth = loopWidthRef.current;

    const step = (time) => {
      if (!pausedRef.current && !dragState.current.dragging) {
        if (lastTime !== null) {
          const dt = time - lastTime;
          const delta = AUTO_SCROLL_SPEED * (dt / 16.66);
          rail.scrollLeft += delta;

          if (loopWidth > 0 && rail.scrollLeft >= loopWidth) {
            rail.scrollLeft -= loopWidth;
          }
        }
      }
      lastTime = time;
      frameId = window.requestAnimationFrame(step);
    };

    frameId = window.requestAnimationFrame(step);

    return () => {
      if (frameId) window.cancelAnimationFrame(frameId);
      if (resumeTimerRef.current) window.clearTimeout(resumeTimerRef.current);
    };
  }, [reducedMotion]);

  return (
    <motion.div
      className="hp-testimonial-rail-wrap"
      variants={sectionReveal}
      initial={reducedMotion ? 'visible' : 'hidden'}
      whileInView="visible"
      viewport={{ once: true, amount: 0.18 }}
    >
      <div className="hp-testimonial-toolbar">
        <div className="hp-testimonial-arrows" aria-label="Testimonial controls">
          <button
            type="button"
            onClick={() => scrollRail(-1)}
            aria-label="Scroll testimonials left"
          >
            <ChevronIcon direction="left" />
          </button>
          <button
            type="button"
            onClick={() => scrollRail(1)}
            aria-label="Scroll testimonials right"
          >
            <ChevronIcon />
          </button>
        </div>
      </div>

      <div
        className={`hp-testimonial-rail${isDragging ? ' is-dragging' : ''}`}
        ref={railRef}
        role="region"
        aria-label="Horizontally scrolling testimonial cards, auto-advancing and draggable"
        tabIndex={0}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={endDrag}
        onPointerLeave={endDrag}
        onClickCapture={suppressClickAfterDrag}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onFocus={handleMouseEnter}
        onBlur={handleMouseLeave}
      >
        {loopedTestimonials.map((testimonial, index) => (
          <article
            className="hp-testimonial-card"
            key={`${testimonial.name}-${index}`}
            aria-hidden={index >= testimonials.length}
          >
            <div className="hp-testimonial-card-top">
              <span className="hp-testimonial-card-number">
                {String((index % testimonials.length) + 1).padStart(2, '0')}
              </span>
              {/* <span className="hp-testimonial-sample-tag">
                {testimonial.context}
              </span> */}
            </div>

            <span className="hp-testimonial-quote-mark" aria-hidden="true">
              “
            </span>

            <blockquote>
              <p>{testimonial.quote}</p>
            </blockquote>

            <footer className="hp-testimonial-person">
              <img
                src={testimonial.image}
                alt=""
                loading="lazy"
                decoding="async"
                referrerPolicy="no-referrer"
              />
              <span>
                <strong>{testimonial.name}</strong>
                <small>{testimonial.role}</small>
              </span>
            </footer>
          </article>
        ))}
      </div>
    </motion.div>
  );
};

const CommitmentsSection = () => (
  <section
    id="built-around-you"
    className="hp-section hp-commitments-section"
    aria-labelledby="commitments-title"
    data-story-anchor
  >
    <div className="container hp-section-layer">
      <div className="hp-section-intro hp-section-intro-centered" data-scroll>
        <div>
          <div className="hp-label">Built around you</div>
          <h2 id="commitments-title" className="hp-section-title">
            Built to understand.
            <span>Designed to adapt.</span>
          </h2>
        </div>

        <p className="hp-section-copy">
          The four signals above describe how Rusiv works internally. These
          four commitments describe what that means for you, the person on
          the other side of the conversation.
        </p>
      </div>

      <div className="hp-commitments-row">
        {commitments.map((item, index) => (
          <article
            className="hp-commitment-item"
            key={item.title}
            data-scroll
            style={{ transitionDelay: `${index * 0.07}s` }}
          >
            <span className="hp-commitment-num">{item.num}</span>
            <h3>{item.title}</h3>
            <p>{item.desc}</p>
          </article>
        ))}
      </div>

      <p className="hp-commitments-footnote" data-scroll>
        See the specific controls this gives you in{' '}
        <a href="/rusiv#privacy" className="hp-text-link-inline">
          Privacy and Control on the product page
        </a>
        .
      </p>
    </div>
  </section>
);

const CapabilitiesSection = () => (
  <section
    id="different-moments"
    className="hp-section hp-capabilities-section"
    aria-labelledby="capabilities-title"
    data-story-anchor
  >
    <div className="container hp-section-layer">
      <div className="hp-section-intro" data-scroll>
        <div>
          <div className="hp-label">Moments</div>
          <h2 id="capabilities-title" className="hp-section-title">
            Same Rusiv.
            <span>Different moments.</span>
          </h2>
        </div>

        <p className="hp-section-copy">
          The same person needs different kinds of help on different days. The
          response should change without making the experience unpredictable.
        </p>
      </div>

      <div className="hp-moments-grid">
        {capabilities.map((item, index) => (
          <article
            className="hp-moment-item"
            key={item.title}
            data-scroll
            style={{ transitionDelay: `${index * 0.06}s` }}
          >
            <span className="hp-moment-num">{item.num}</span>
            <h3>{item.title}</h3>
            <p>{item.desc}</p>
          </article>
        ))}
      </div>
    </div>
  </section>
);

const ShiftSection = () => (
  <section
    id="the-shift"
    className="hp-section hp-shift-section"
    aria-labelledby="shift-title"
    data-story-anchor
  >
    <div className="container hp-section-layer">
      <div className="hp-section-intro" data-scroll>
        <div>
          <div className="hp-label">The shift</div>
          <h2 id="shift-title" className="hp-section-title">
            From generic to
            <span>personal.</span>
          </h2>
        </div>

        <p className="hp-section-copy">
          A quick preview of what changes when the person is understood, not
          just the prompt.
        </p>
      </div>

      <div className="hp-shift-grid" data-scroll>
        <article className="hp-shift-panel hp-shift-panel-dim">
          <header className="hp-shift-panel-header">
            <span className="hp-shift-dot hp-shift-dot-muted" />
            Today: the same result for everyone
          </header>

          <div className="hp-shift-panel-body">
            <div className="hp-search-bar">
              <SearchIcon />
              <span>explain photosynthesis…</span>
            </div>

            {[85, 70, 90, 60].map((width, index) => (
              <div className="hp-result-item" key={width}>
                <span>{['Wikipedia', 'Forbes', 'Nature', 'Reddit'][index]}</span>
                <i style={{ width: `${width}%` }} />
                <b />
              </div>
            ))}

            <p className="hp-shift-verdict">
              Fragmented. Generic. You still do the work.
            </p>
          </div>
        </article>

        <article className="hp-shift-panel hp-shift-panel-personal">
          <header className="hp-shift-panel-header">
            <span className="hp-shift-dot hp-shift-dot-active" />
            Tomorrow: Rusiv understands the person
          </header>

          <div className="hp-shift-panel-body">
            <div className="hp-convo-row">
              <span className="hp-convo-role">You</span>
              <p>Explain photosynthesis to me.</p>
            </div>

            <div className="hp-convo-row">
              <span className="hp-convo-role hp-convo-role-rusiv">Rusiv</span>
              <div>
                <p>
                  You prefer real world comparisons, so think of a leaf as a
                  tiny solar panel that turns sunlight into usable food.
                </p>
                <span className="hp-convo-tag">Matched to how you learn</span>
              </div>
            </div>

            <p className="hp-shift-verdict hp-shift-verdict-active">
              Clear. Personal. Made for this moment.
            </p>
          </div>
        </article>
      </div>

      <a href="/rusiv#difference" className="hp-text-link hp-shift-full-link">
        See the full comparison
        <ArrowIcon />
      </a>
    </div>
  </section>
);

const useVisibilitySequence = (
  targetRef,
  sequence,
  reducedMotionStep,
  initialDelay = 650
) => {
  const [step, setStep] = useState(0);
  const [finished, setFinished] = useState(false);
  const [replayToken, setReplayToken] = useState(0);

  useEffect(() => {
    const reducedMotion =
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (reducedMotion) {
      setStep(reducedMotionStep);
      setFinished(true);
      return undefined;
    }

    let sequenceIndex = 0;
    let timer;
    let running = false;
    let hasPlayed = false;

    const advance = () => {
      if (!running) return;
      if (sequenceIndex >= sequence.length) {
        running = false;
        hasPlayed = true;
        setFinished(true);
        return;
      }

      const [nextStep, delay] = sequence[sequenceIndex];
      sequenceIndex += 1;

      timer = window.setTimeout(() => {
        if (!running) return;
        setStep(nextStep);
        advance();
      }, delay);
    };

    const start = () => {
      if (running || hasPlayed) return;
      running = true;
      timer = window.setTimeout(advance, initialDelay);
    };

    const stop = () => {
      running = false;
      window.clearTimeout(timer);
    };

    const node = targetRef.current;
    let observer;

    if (node && 'IntersectionObserver' in window) {
      observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) start();
          else stop();
        },
        { threshold: 0.24 }
      );
      observer.observe(node);
    } else {
      start();
    }

    return () => {
      stop();
      observer?.disconnect();
    };
  }, [initialDelay, reducedMotionStep, sequence, targetRef, replayToken]);

  const replay = () => {
    setStep(0);
    setFinished(false);
    setReplayToken((token) => token + 1);
  };

  return [step, replay, finished];
};


// Screens at least this wide show the animated dotted logo beside the hero
// copy. Keep this value aligned with the matching CSS breakpoint.
const HERO_VISUAL_BREAKPOINT = 1100;

const useIsViewportAtLeast = (breakpointPx) => {
  const query = `(min-width: ${breakpointPx}px)`;

  const [matches, setMatches] = useState(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return true;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;

    const mediaQueryList = window.matchMedia(query);
    const handleChange = (event) => setMatches(event.matches);

    setMatches(mediaQueryList.matches);

    if (mediaQueryList.addEventListener) {
      mediaQueryList.addEventListener('change', handleChange);
    } else {
      mediaQueryList.addListener(handleChange);
    }

    return () => {
      if (mediaQueryList.removeEventListener) {
        mediaQueryList.removeEventListener('change', handleChange);
      } else {
        mediaQueryList.removeListener(handleChange);
      }
    };
  }, [query]);

  return matches;
};

const HomePage = () => {
  const navigate = useNavigate();
  const heroRef = useRef(null);
  const typingRef = useRef(null);
  const productChatRef = useRef(null);
  const storyRef = useRef(null);

  useScrollReveal();

  const showHeroVisual = useIsViewportAtLeast(HERO_VISUAL_BREAKPOINT);

  const [mounted, setMounted] = useState(false);
  const [typedPhrase, setTypedPhrase] = useState('');
  const [typingDone, setTypingDone] = useState(false);
  const [subtitleVisible, setSubtitleVisible] = useState(false);
  const [buttonsVisible, setButtonsVisible] = useState(false);
  const [particlesActive, setParticlesActive] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);

  // Preserve the current hero entrance behaviour and reset the page position,
  // unless a deep link (e.g. a shared FAQ hash) requests a specific spot.
  useEffect(() => {
    const timer = window.setTimeout(() => setMounted(true), 100);
    if (!window.location.hash) {
      window.scrollTo(0, 0);
    }

    return () => window.clearTimeout(timer);
  }, []);

  // Open and scroll to a specific FAQ when the page is opened with a
  // matching URL hash (e.g. shared from a support link).
  useEffect(() => {
    const hash = window.location.hash.replace('#', '');
    if (!hash) return undefined;

    const index = faqs.findIndex((faq) => slugify(faq.question) === hash);
    if (index === -1) return undefined;

    const timer = window.setTimeout(() => {
      setOpenFaq(index);
      document.getElementById(hash)?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }, 700);

    return () => window.clearTimeout(timer);
  }, []);

  // Preserve the current one-pass hero typing sequence.
  useEffect(() => {
    if (!mounted) return undefined;

    const phrases = [
      'begins with you.',
      'learns from you.',
      'adapts to you.',
      'grows with you.',
    ];
    const reducedMotion =
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (reducedMotion) {
      setTypedPhrase(phrases[phrases.length - 1]);
      setTypingDone(true);
    } else {
      let phraseIndex = 0;
      let characterIndex = 0;
      let deleting = false;

      const TYPE_MS = 55;
      const DELETE_MS = 24;
      const HOLD_MS = 750;
      // Slightly longer pause after the final phrase before the whole
      // sequence loops back to the first one, so the loop boundary reads
      // as an intentional beat rather than an identical mid-cycle pause.
      const LOOP_HOLD_MS = 1400;
      const GAP_MS = 250;

      const tick = () => {
        const current = phrases[phraseIndex];
        const isLastPhrase = phraseIndex === phrases.length - 1;

        if (deleting) {
          characterIndex -= 1;
          setTypedPhrase(current.slice(0, characterIndex));

          if (characterIndex === 0) {
            deleting = false;
            phraseIndex = (phraseIndex + 1) % phrases.length;
            typingRef.current = window.setTimeout(tick, GAP_MS);
          } else {
            typingRef.current = window.setTimeout(tick, DELETE_MS);
          }
        } else {
          characterIndex += 1;
          setTypedPhrase(current.slice(0, characterIndex));

          if (characterIndex === current.length) {
            setTypingDone(isLastPhrase);
            deleting = true;
            typingRef.current = window.setTimeout(
              tick,
              isLastPhrase ? LOOP_HOLD_MS : HOLD_MS
            );
          } else {
            setTypingDone(false);
            typingRef.current = window.setTimeout(tick, TYPE_MS);
          }
        }
      };

      typingRef.current = window.setTimeout(tick, 150);
    }

    const subtitleTimer = window.setTimeout(
      () => setSubtitleVisible(true),
      600
    );
    const buttonsTimer = window.setTimeout(
      () => setButtonsVisible(true),
      1200
    );
    const particlesTimer = window.setTimeout(
      () => setParticlesActive(true),
      2000
    );

    return () => {
      window.clearTimeout(typingRef.current);
      window.clearTimeout(subtitleTimer);
      window.clearTimeout(buttonsTimer);
      window.clearTimeout(particlesTimer);
    };
  }, [mounted]);

  const [productChatStep, replayProductChat, productChatFinished] = useVisibilitySequence(
    productChatRef,
    productChatSequence,
    6,
    550
  );

  return (
    <div
      className="hp-page"
      style={{ background: 'var(--bg-primary)', overflowX: 'hidden' }}
    >
      {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ HERO ━━━━━━━━━━━━━━━━━━━━━━━ */}
      <section ref={heroRef} className="hp-hero">
        {showHeroVisual && (
          <div className="hp-hero-visual" aria-hidden="true">
            <HeroParticles isActive={particlesActive} />
          </div>
        )}

        <div className="hp-hero-content">
          <div
            className="hp-hero-eyebrow"
            style={{
              opacity: mounted ? 1 : 0,
              transform: mounted ? 'translateY(0)' : 'translateY(16px)',
              transition: 'opacity 0.8s var(--ease-out), transform 0.8s var(--ease-out)',
            }}
          >
            <span className="hp-hero-eyebrow-dot" />
            Allyverto
          </div>

          <h1
            className="hp-hero-headline"
            style={{
              opacity: mounted ? 1 : 0,
              transform: mounted ? 'translateY(0)' : 'translateY(30px)',
              transition: 'opacity 0.9s var(--ease-out) 0.05s, transform 0.9s var(--ease-out) 0.05s',
            }}
          >
            Intelligence that
            <br />
            <span
              data-typing-state={typingDone ? 'done' : 'typing'}
              className="hp-hero-typed"
            >
              {typedPhrase}
              <span className="hp-cursor" />
            </span>
          </h1>

          <p
            className="hp-hero-subtitle"
            style={{
              opacity: subtitleVisible ? 1 : 0,
              transform: subtitleVisible ? 'translateY(0)' : 'translateY(24px)',
              transition: 'opacity 0.8s var(--ease-out), transform 0.8s var(--ease-out)',
            }}
          >
            Allyverto is building personal intelligence, AI that begins with
            an understanding of you, not a generic version of you. Rusiv is
            our first product, and the first proof of that idea.
          </p>

          <div
            className="hp-hero-actions"
            style={{
              opacity: buttonsVisible ? 1 : 0,
              transform: buttonsVisible ? 'translateY(0)' : 'translateY(18px)',
              transition: 'opacity 0.8s var(--ease-out), transform 0.8s var(--ease-out)',
            }}
          >
            <a
              href={RUSIV_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-primary"
            >
              Try Rusiv
            </a>
            <a href="/vision" className="btn">
              Explore Our Vision
            </a>
          </div>
        </div>
      </section>

      <div className="hp-story" ref={storyRef}>
        <StorySignal targetRef={storyRef} />

        <section
          id="what-we-build"
          className="hp-section hp-build"
          aria-labelledby="what-we-build-title"
          data-story-anchor
        >
        <div className="container hp-section-layer">
          <div className="hp-section-intro">
            <div>
              <div className="hp-label">What Allyverto builds</div>
              <h2 id="what-we-build-title" className="hp-section-title">
                Intelligence that works
                <span>with the person.</span>
              </h2>
            </div>

            <p className="hp-section-copy">
              Explore the four behaviours that turn an AI response into a
              continuous, useful relationship with the person using it.
            </p>
          </div>

          <BuildExperience />
        </div>
      </section>

      <CommitmentsSection />

      <section
        className="hp-section hp-rusiv"
        aria-labelledby="rusiv-title"
        data-story-anchor
      >
        <div className="container hp-rusiv-grid hp-section-layer">
          <div className="hp-rusiv-copy">
            <div className="hp-label">Our first product</div>
            <h2 id="rusiv-title" className="hp-section-title">
              Meet Rusiv.
              <span>Your intelligent counterpart.</span>
            </h2>

            <p className="hp-section-copy">
              Rusiv is being built to understand the person behind the
              question, carry relevant context across conversations, and use
              memory, current information, documents, and tools when they make
              the answer genuinely better.
            </p>

            <div className="hp-rusiv-summary">
              <div>
                <strong>Continuity</strong>
                <span>Useful context can continue across conversations.</span>
              </div>
              <div>
                <strong>Current information</strong>
                <span>Live information can be brought in when it matters.</span>
              </div>
              <div>
                <strong>Tools when needed</strong>
                <span>
                  Search, documents, calculation, and more can join the
                  conversation.
                </span>
              </div>
            </div>

            <a href={RUSIV_URL} className="hp-text-link">
              Explore Rusiv
              <ArrowIcon />
            </a>
          </div>

          <div className="hp-rusiv-demo-wrap">
            <div className="hp-chat-window" ref={productChatRef}>
              <div className="hp-chat-header">
                <div className="hp-chat-header-left">
                  <span className="hp-chat-status-dot" />
                  <span className="hp-chat-title">Rusiv</span>
                </div>
                {productChatFinished ? (
                  <button
                    type="button"
                    className="hp-chat-replay-btn"
                    onClick={replayProductChat}
                  >
                    Replay demo
                  </button>
                ) : (
                  <span className="hp-chat-demo-label">Illustrative demo</span>
                )}
              </div>

              <div className="hp-chat-body">
                <div className="hp-chat-row hp-chat-row-user">
                  <div
                    className={`hp-chat-bubble hp-chat-bubble-user ${
                      productChatStep >= 1
                        ? 'hp-chat-bubble-visible'
                        : 'hp-chat-bubble-hidden'
                    }`}
                  >
                    We were planning my research workflow yesterday. Where did
                    we leave off?
                  </div>
                </div>

                <div className="hp-chat-row hp-chat-row-ai">
                  <div
                    className={`hp-typing hp-chat-ai-layer ${
                      productChatStep === 2
                        ? 'hp-chat-layer-visible'
                        : 'hp-chat-layer-hidden'
                    }`}
                    aria-hidden={productChatStep !== 2}
                  >
                    <span />
                    <span />
                    <span />
                  </div>

                  <div
                    className={`hp-chat-bubble hp-chat-bubble-ai hp-chat-ai-layer ${
                      productChatStep >= 3
                        ? 'hp-chat-layer-visible'
                        : 'hp-chat-layer-hidden'
                    }`}
                  >
                    <span className="hp-chat-role">Rusiv</span>
                    You had narrowed it to two options: a source-first workflow
                    and a faster synthesis-first workflow. You wanted to compare
                    them before choosing one.
                    <span className="hp-chat-context">
                      Used relevant conversation context
                    </span>
                  </div>
                </div>

                <div className="hp-chat-row hp-chat-row-user">
                  <div
                    className={`hp-chat-bubble hp-chat-bubble-user ${
                      productChatStep >= 4
                        ? 'hp-chat-bubble-visible'
                        : 'hp-chat-bubble-hidden'
                    }`}
                  >
                    Check which approach is better for current AI research.
                  </div>
                </div>

                <div className="hp-chat-row hp-chat-row-ai">
                  <div
                    className={`hp-typing hp-chat-ai-layer ${
                      productChatStep === 5
                        ? 'hp-chat-layer-visible'
                        : 'hp-chat-layer-hidden'
                    }`}
                    aria-hidden={productChatStep !== 5}
                  >
                    <span />
                    <span />
                    <span />
                  </div>

                  <div
                    className={`hp-chat-bubble hp-chat-bubble-ai hp-chat-ai-layer ${
                      productChatStep === 6
                        ? 'hp-chat-layer-visible'
                        : 'hp-chat-layer-hidden'
                    }`}
                  >
                    <span className="hp-chat-role">Rusiv</span>
                    I would use the source-first workflow here. This question
                    depends on current evidence, so I would search first,
                    compare the strongest sources, and then synthesise the
                    answer.
                    <span className="hp-chat-context">
                      Memory + current information
                    </span>
                  </div>
                </div>
              </div>

              <div className="hp-chat-footer">
                <div className="hp-chat-input-mock">Ask Rusiv anything…</div>
                <button
                  className="hp-chat-send-btn"
                  type="button"
                  aria-label="Illustrative send button"
                  disabled
                >
                  <SendIcon />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <CapabilitiesSection />

      <ShiftSection />

      <section
        className="hp-section hp-testimonials"
        aria-labelledby="testimonials-title"
        data-story-anchor
      >
        <div className="container hp-section-layer">
          <div className="hp-testimonials-heading">
            <div className="hp-label">Testimonials</div>
            <h2 id="testimonials-title" className="hp-section-title">
              Early impressions
              <span>of Rusiv.</span>
            </h2>
          </div>

          <TestimonialRail />
        </div>
      </section>

      <section
        className="hp-section hp-faq"
        aria-labelledby="faq-title"
        data-story-anchor
      >
        <div className="container hp-faq-grid hp-section-layer">
          <div className="hp-faq-intro">
            <div className="hp-label">Frequently asked questions</div>
            <h2 id="faq-title" className="hp-section-title">
              Questions people
              <span>ask us often.</span>
            </h2>
            <p className="hp-section-copy">
              Quick answers about Allyverto, Rusiv, and the kind of intelligence
              we are building.
            </p>
          </div>

          <div className="hp-faq-list">
            {faqs.map((faq, index) => {
              const isOpen = openFaq === index;
              const slug = slugify(faq.question);

              return (
                <article
                  className={`hp-faq-card ${isOpen ? 'is-open' : ''}`}
                  key={faq.question}
                  id={slug}
                >
                  <button
                    type="button"
                    className="hp-faq-question"
                    onClick={() => {
                      const nextOpen = isOpen ? null : index;
                      setOpenFaq(nextOpen);

                      if (nextOpen !== null) {
                        window.history.replaceState(null, '', `#${slug}`);
                      } else if (window.location.hash === `#${slug}`) {
                        window.history.replaceState(
                          null,
                          '',
                          window.location.pathname + window.location.search
                        );
                      }
                    }}
                    aria-expanded={isOpen}
                  >
                    <span>{faq.question}</span>
                    <span className="hp-faq-plus" aria-hidden="true">
                      +
                    </span>
                  </button>

                  <div className="hp-faq-answer-wrap">
                    <div className="hp-faq-answer">
                      <p>{faq.answer}</p>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      </div>

      {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ CTA ━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <section
        className="hp-final-cta"
        style={{ padding: 'clamp(6rem, 12vw, 10rem) 0', background: 'var(--bg-secondary)' }}
      >
        <div
          className="container-narrow"
          style={{ textAlign: 'center', position: 'relative' }}
        >
          <div data-scroll>
            <div className="hp-label">What's Next</div>
            <h2
              style={{
                fontFamily: 'var(--font-serif)',
                fontStyle: 'italic',
                fontSize: 'clamp(2.5rem, 6vw, 4.5rem)',
                lineHeight: '1.1',
                marginBottom: '1.25rem',
                color: 'var(--text-primary)',
              }}
            >
              Your AI should not meet YOU like a stranger.
            </h2>
            <p
              style={{
                fontSize: '1.08rem',
                lineHeight: '1.75',
                color: 'var(--text-secondary)',
                maxWidth: '460px',
                margin: '0 auto 2.5rem',
              }}
            >
              Learn how Allyverto thinks, or see where this is all heading.
            </p>
            <div className="hp-final-cta-buttons">
              <button className="btn alluse-button alluse-button-home"
                onClick={() => navigate("/about")}>
                  Learn about Allyverto
              </button>
              <button className="btn alluse-button alluse-button-home" 
                onClick={() => navigate("/vision")}>
                  Explore Our Vision
              </button>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
};

export default HomePage;
